import React from 'react';
import type { ComponentType, SVGProps } from 'react';

import { Block } from '@actual-app/components/block';
import { theme } from '@actual-app/components/theme';
import { View } from '@actual-app/components/view';

import { ItemContent } from '#components/sidebar/ItemContent';

type Props = {
  title: string;
  Icon:
    | ComponentType<SVGProps<SVGElement>>
    | ComponentType<SVGProps<SVGSVGElement>>;
  to: string;
  /** Quantos itens esperam ação. Zero (ou ausente) não desenha nada. */
  contador?: number;
};

/**
 * Um item da barra lateral.
 *
 * ⛔ Não usa o `sidebar/Item.tsx` do Actual, e não o edita. Dois motivos:
 * o `Item` deles marca o item selecionado com uma **barra vertical de 4px à
 * esquerda**, e o MULTICAP (a referência que o dono mandou copiar) marca com
 * uma **pílula de fundo suave** — medido nas telas dele; e o `Item` não tem
 * prop de contador, que a Pendências precisa. Este componente replica a
 * estrutura e reusa `ItemContent`, que é importado, nunca modificado.
 *
 * ⚠️ Contador zero não vira "0" na tela. Fila vazia é o estado saudável, e um
 * zero permanente no menu vira ruído que se aprende a ignorar — junto com o
 * número quando ele finalmente importar.
 */
export function ItemDoMenu({ title, Icon, to, contador = 0 }: Props) {
  const base = {
    fontSize: 15,
    fontWeight: 500,
    paddingTop: 11,
    paddingBottom: 11,
    paddingLeft: 14,
    paddingRight: 12,
    marginLeft: 10,
    marginRight: 10,
    borderRadius: 10,
    textDecoration: 'none',
    color: theme.sidebarItemText,
    ':hover': { backgroundColor: theme.sidebarItemBackgroundHover },
  };

  return (
    <View style={{ flexShrink: 0 }}>
      <ItemContent
        style={base}
        activeStyle={{
          backgroundColor: theme.sidebarItemBackgroundHover,
          color: theme.sidebarItemTextSelected,
          fontWeight: 600,
        }}
        to={to}
        onClick={undefined}
      >
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 12 }}>
          <Icon width={18} height={18} />
          <Block>{title}</Block>
          <View style={{ flex: 1 }} />
          {contador > 0 && (
            <Block
              data-testid="contador-pendencias"
              style={{
                fontSize: 12,
                fontWeight: 700,
                fontVariantNumeric: 'tabular-nums',
                lineHeight: '20px',
                minWidth: 20,
                paddingLeft: 6,
                paddingRight: 6,
                borderRadius: 10,
                textAlign: 'center',
                backgroundColor: theme.sidebarItemTextSelected,
                color: theme.sidebarBackground,
              }}
            >
              {contador}
            </Block>
          )}
        </View>
      </ItemContent>
    </View>
  );
}
